import apiClient from './client'

export interface CableType {
  id: number
  name: string
  code: string
  description?: string
  icon?: string
  color?: string
  sort_order: number
  is_active: boolean
  created_at?: string
  updated_at?: string
}

export const getCableTypes = async (): Promise<CableType[]> => {
  const response = await apiClient.get('/api/cableTypes')
  
  // Check if response.data is the array we need (from interceptor)
  if (Array.isArray(response.data)) {
    return response.data
  }
  
  // Fallback for raw response format
  if (response && response.data && response.data.success) {
    return response.data.data
  }
  
  throw new Error('Failed to fetch cable types')
}

export const getCableType = async (id: number): Promise<CableType> => {
  const response = await apiClient.get(`/api/cableTypes/${id}`)
  
  // API client interceptor already returns response.data.data directly in response.data
  if (response && response.data && typeof response.data === 'object' && 'id' in response.data) {
    return response.data as CableType
  }
  
  // Fallback for raw response format
  if (response && response.data && response.data.success) {
    return response.data.data
  }
  
  throw new Error('Cable type not found')
}

export const createCableType = async (data: Omit<CableType, 'id' | 'created_at' | 'updated_at'>): Promise<CableType> => {
  const response = await apiClient.post('/api/cableTypes', data)
  
  // Check if response.data contains the created ID (from interceptor)
  if (response && response.data && typeof response.data === 'object' && 'id' in response.data) {
    return {
      ...data,
      id: parseInt(response.data.id as string),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    } as CableType
  }
  
  // API client interceptor already returns response.data.data directly
  if (response && typeof response === 'object' && 'id' in response) {
    return {
      ...data,
      id: parseInt(response.id as string),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    } as CableType
  }
  
  // Fallback for raw response format
  if (response && response.data && response.data.success) {
    return {
      ...data,
      id: parseInt(response.data.data.id as string),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    } as CableType
  }
  
  throw new Error('Failed to create cable type')
}

export const updateCableType = async (id: number, data: Partial<CableType>): Promise<void> => {
  await apiClient.put(`/api/cableTypes/${id}`, data)
  
  // API client interceptor handles success response format
  // If no error thrown, operation was successful
}

export const deleteCableType = async (id: number): Promise<void> => {
  await apiClient.delete(`/api/cableTypes/${id}`)
  
  // API client interceptor handles success response format
  // If no error thrown, operation was successful
}

